/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectIsPrimary,
  selectSegmentGroup1,
  selectSegmentGroup2,
  selectSegmentSetAName,
  selectSegmentSetBName,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectIsTrendDeAggregate,
  selectTrendGranularity,
  selectTrendHotelFocusOn,
  selectTrendHotelNormalizedView,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
import MuiTabular from 'modules/common/components/mui-tabular';
import { IconButton, Typography } from '@mui/material';
import useAbortableRequest from 'modules/common/hooks/use-abortable-request';
import { isRequestCanceled } from 'modules/common/utils/request';
import { API, EXPORT_OPTIONS } from 'modules/dashboard/constants';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { AssistantIcon } from 'modules/common/components';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import DownloadIcon from '@mui/icons-material/Download';
import { useDebouncedCallback } from 'use-debounce';
import { BREAKDOWN_LIST_GETTER_COLUMNS } from '../../../constants';
import {
  buildSegmentSchema,
  buildSegmentTimeSchema,
  buildTrendFilters,
  buildTrendSegmentHeaderData,
  buildCombinedVisualHeaderData,
  getBreakdownName,
  getSegmentColumns,
  groupDataWithParent,
  handleCeruleanLogging,
} from '../../../function';
import TabularFilterHeader from './filter-header';
import { selectTrendData, selectTrendDataResponseId } from '../../../selectors';
import { trendTabularActions } from '../../../slice';

/**
 * Trend Segment tabular visual report with filters
 */

const MuiTrendSegmentTabularWidget = ({ setIsTimeLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const isBreakdown = useSelector(selectIsPrimary);
  const isTrendDeAggregate = useSelector(selectIsTrendDeAggregate);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const segmentGroupA = useSelector(selectSegmentGroup1);
  const segmentGroupB = useSelector(selectSegmentGroup2);
  const segmentGroupAName = useSelector(selectSegmentSetAName);
  const segmentGroupBName = useSelector(selectSegmentSetBName);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const trendGranularity = useSelector(selectTrendGranularity);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const normalization = useSelector(selectTrendHotelNormalizedView);
  const dispatch = useDispatch();
  const abortableRequest = useAbortableRequest();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  //
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      segmentGroupA,
      segmentGroupB,
      segmentGroupAName,
      segmentGroupBName,
      resetBookingDate,
      trendGranularity,
      hotelFocusOn,
      partialHours,
      lastBookingDate,
      normalization
    });
    //
    const columns = getSegmentColumns({
      isPercentageChange,
      isMovingAverage,
      isCumulative,
      isWeeklyView,
      isTrendDeAggregate,
      segmentGroupA,
      segmentGroupB,
      isHotelComparison: hotelFocusOn?.id !== hotels?.id,
    });
    //
    let start;
    try {
      setIsTimeLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Time Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      // fetch breakdown list
      const breakdownListData = await abortableRequest(API.GET_JS_VISUAL_DATA, {
        organizationId,
        columns: BREAKDOWN_LIST_GETTER_COLUMNS,
        filters,
      });
      //
      const breakdown = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
      const breakdownList = breakdownListData?.map((item) => item?.[breakdown]);
      if (filters?.breakDownList?.conditions) {
        const isTotalInclude = filters.breakDownList.conditions.values
          .map((v) => v.replace(/\u200B/g, '').trim())
          .includes('Total');
        filters.breakDownList.conditions.values = [
          ...breakdownList,
          ...(isTotalInclude ? ['Total'] : []),
        ];
      }
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-segment',
      });
      //
      dispatch(
        trendTabularActions.getTrendData({
          organizationId,
          columns,
          filters,
        })
      );
    } catch (error) {
      if (isRequestCanceled(error)) return;
      handleCeruleanLogging({
        message: `Error fetching trend time tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsTimeLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    fetchData();
  }, [
    bookingDate,
    currencyType,
    hotels,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsTimeLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    startDate,
    endDate,
    isBreakdown,
    segmentGroupA,
    segmentGroupB,
    segmentGroupAName,
    segmentGroupBName,
    trendGranularity,
    hotelFocusOn,
    partialHours,
    lastBookingDate,
    normalization
  ]);
  //
  const headerData = useMemo(
    () =>
      isTrendDeAggregate
        ? buildCombinedVisualHeaderData({
            targetStartDate,
            targetEndDate,
            hotelName: hotels?.label,
            bookingDate,
            currencyType,
            pmsSync: pms,
            isCumulative,
            isPercentageChange,
            dateComparison,
            isWeeklyView,
            isMovingAverage,
            resetBookingDate,
            symmetricComparisonToggle,
            isHotelComparison: hotelFocusOn?.id !== hotels?.id,
            hotelFocusOn: hotelFocusOn?.label,
            isComparisonHotelSet: Boolean(hotelFocusOn?.hotels),
            partialHours,
            lastBookingDate,
            alignmentToggle,
          })
        : buildTrendSegmentHeaderData({
            hotelName: hotels?.label,
            bookingDate,
            targetStartDate,
            targetEndDate,
            alignmentToggle,
            symmetricComparisonToggle,
            currencyType,
            pmsSync: pms,
            dateComparison,
            startDate,
            endDate,
            isBreakdown,
            isCumulative,
            isPercentageChange,
            isWeeklyView,
            isMovingAverage,
            resetBookingDate,
          }),
    [
      hotels,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      dateComparison,
      isBreakdown,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      isTrendDeAggregate,
      trendGranularity,
      hotelFocusOn?.id,
      partialHours,
      lastBookingDate,
    ]
  );

  const breakdownName = getBreakdownName(isDetailed, visualFilters, chartBreakdown);
  const data = isTrendDeAggregate
    ? groupDataWithParent(trendData, breakdownName, isWeeklyView)
    : trendData;
  const tableSchema = isTrendDeAggregate
    ? buildSegmentTimeSchema(data?.[0], isWeeklyView, isCumulative)
    : buildSegmentSchema(data?.[0], isWeeklyView);
  //
  return (
    <Box px={10}>
      {!trendData ? (
        <Typography fontWeight={500}>Loading Data...</Typography>
      ) : (
        <Box>
          <Box display="flex" justifyContent="end">
            <IconButton
              size="medium"
              onClick={() => setIsProcessing(true)}
              sx={EXPORT_BUTTON_STYLE}
            >
              <Typography
                sx={{
                  mr: 1,
                }}
              >
                Export Data
              </Typography>
              <DownloadIcon />
            </IconButton>
          </Box>
          <MuiTabular
            ref={tableRef}
            columns={tableSchema}
            isProcessing={isProcessing}
            isLoadingData={!isLoaded}
            setIsProcessing={setIsProcessing}
            isAggregate={!(isMovingAverage || isPercentageChange)}
            headerComponent={
              <TabularFilterHeader
                title={headerData?.title}
                hotelName={headerData?.hotelName}
                isHotelSet={Boolean(hotels?.hotels)}
                sections={headerData?.sections}
                comparisonHotelName={headerData?.comparisonHotelName}
                isComparisonHotelSet={headerData?.isComparisonHotelSet}
                chunkSize={isTrendDeAggregate && hotelFocusOn?.id === hotels?.id ? 2 : 1}
                interpreterFragment={
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || isExporting}
                    isCerulean
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
            }
            data={data}
            headerFiltersInfo={headerData}
            customBackgroundStyle={{ paddingLeft: 3, maxWidth: { md: '72vw', lg: '76vw' } }}
            customHeaderStyle={{ lineHeight: 1.2 }}
            fileName="TVT_Daily"
            primaryTextColor={isPercentageChange ? '#000' : undefined}
            secondaryTextColor={isPercentageChange || isTrendDeAggregate ? '#000' : undefined}
            removeEmptyColumns
          />
        </Box>
      )}
    </Box>
  );
};
//
export default MuiTrendSegmentTabularWidget;
